import { mergeAttributes, Node } from "@tiptap/core";
import { nanoid } from "nanoid";
import { Fragment, Node as ProseMirrorNode, Slice } from "prosemirror-model";
import {
  EditorState,
  PluginKey,
  Selection,
  TextSelection,
} from "prosemirror-state";
import { nodePasteRuleHardBreak } from "./pastedRuleHardBreak";
import { nodePasteRuleLink } from "./pastedRuleLink";
import { nodePasteRuleParagraph } from "./pastedRuleParagraph";
import { nodePasteRuleTrim } from "./pastedRuleTrim";
import { Suggestion, SuggestionOptions } from "./suggestion";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    suggestion: {
      /**
       * Add an URL as a mention
       */
      addURL: (attributes: {
        marks: string[];
        id: string;
        label?: string;
        colorLink: string;
        autoBoldLinks: boolean;
        tooltipId: string;
      }) => ReturnType;
      /**
       * Add an URL as a mention based on a range
       */
      addURLInRange: (attributes: {
        marks: string[];
        from: number;
        to: number;
        id: string;
        label?: string;
        colorLink: string;
        autoBoldLinks: boolean;
        tooltipId: string;
      }) => ReturnType;
      /**
       * Remove an URL as a mention
       */

      removeURL: () => ReturnType;
    };
  }
}

const getNodeBeforeCursor = (selection: Selection) =>
  selection instanceof TextSelection
    ? selection.$cursor!.nodeBefore
    : undefined;

// Get the current marks at the cursor position as an [string]
export function selectionMarks(state: EditorState, schema: any) {
  const { $from, $to } = state.selection;
  const availableMarks = schema.marks;
  const availableMarksLength = Object.keys(availableMarks).length;
  const currentMarks = [] as string[];
  // No selection, pick the current marks from the text before the cursor
  if ($from.pos === $to.pos) {
    const nodeBefore = getNodeBeforeCursor(state.selection);
    if (nodeBefore) {
      currentMarks.push(...nodeBefore.marks.map((m) => m.type.name));
    }
    return currentMarks;
  }
  state.doc.nodesBetween($from.pos, $to.pos, (node) => {
    if (availableMarksLength === currentMarks.length) {
      return;
    }
    if (node.marks.length !== 0) {
      for (let i = 0; i < node.marks.length; i += 1) {
        const nodeType = node.marks[i].type.name;
        if (currentMarks.indexOf(nodeType) === -1) {
          currentMarks.push(nodeType);
        }
      }
    }
  });
  return currentMarks;
}

const URL_REGEX =
  /(h|H)ttps?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/g;
export function isURL(text: string) {
  let notUrlItems = text.replace(URL_REGEX, "");

  if (notUrlItems.length > 0) return false;

  return true;
}

const NOT_TRIMMED_URL_REGEX =
  /( )*(h|H)ttps?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)( )*/g;
export function isNotTrimmedURL(text: string) {
  let notUrlItems = text.replace(NOT_TRIMMED_URL_REGEX, "");

  if (notUrlItems.length > 0) return false;

  return true;
}

export type MentionOptions = {
  HTMLAttributes: Record<string, any>;
  renderLabel: (props: {
    options: MentionOptions;
    node: ProseMirrorNode;
  }) => string;
  suggestion: Omit<SuggestionOptions, "editor">;
};

export const MentionPluginKey = new PluginKey("mention");

export const TiptapAutoLink = Node.create<MentionOptions>({
  name: "mention",

  defaultOptions: {
    HTMLAttributes: {},
    renderLabel: ({ options, node }) => `${node.attrs.label ?? node.attrs.id}`,
    suggestion: {
      char: "h",
      pluginKey: MentionPluginKey,
      command: ({ editor, range, props }) => {
        // increase range.to by one when the next node is of type "text"
        // and starts with a space character
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        const overrideSpace = nodeAfter?.text?.startsWith(" ");

        if (overrideSpace) {
          range.to += 1;
        }

        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: "mention",
              attrs: props,
            },
          ])
          .run();
      },

      allow: ({ editor, range }) => {
        return editor.can().insertContentAt(range, { type: "mention" });
      },
    },
  },

  group: "inline",

  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("href"),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {};
          }

          return {
            "data-id": attributes.id,
            href: attributes.id,
          };
        },
      },

      label: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-label") ||
          element.textContent ||
          element.getAttribute("href"),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {};
          }

          return {
            "data-label": attributes.label,
          };
        },
      },

      colorLink: {
        default: this.options.HTMLAttributes.colorLink,
        parseHTML: (element) => this.options.HTMLAttributes.colorLink,
        renderHTML: (attributes) => {
          if (!attributes.colorLink) {
            return {};
          }

          return {
            colorLink: this.options.HTMLAttributes.colorLink,
          };
        },
      },

      autoBoldLinks: {
        default: this.options.HTMLAttributes.autoBoldLinks,
        parseHTML: (element) => this.options.HTMLAttributes.autoBoldLinks,
        renderHTML: (attributes) => {
          if (!attributes.autoBoldLinks) {
            return {};
          }

          return {
            autoBoldLinks: this.options.HTMLAttributes.autoBoldLinks,
          };
        },
      },

      tooltipId: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-tooltip-id") || nanoid(),
        renderHTML: (attributes) => {
          if (!attributes.tooltipId) {
            return {};
          }

          return {
            "data-tooltip-id": attributes.tooltipId,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "a[href]",
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "a",
      // As the data-id is the URL, merge the attributes and add it to the href property
      mergeAttributes(
        {
          href: `${HTMLAttributes["data-id"]}`,
          style: `color: ${
            HTMLAttributes["colorLink"] || "inherit"
          }; font-size: inherit; text-decoration: none !important;`,
          target: "_blank",
        },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      this.options.renderLabel({
        options: this.options,
        node,
      }),
    ];
  },

  renderText({ node }) {
    return this.options.renderLabel({
      options: this.options,
      node,
    });
  },

  addPasteRules() {
    return [
      nodePasteRuleLink(
        /(h|H)ttps?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/g,
        this.type,
        (match, level) => ({
          id: match,
          label: match,
          colorLink: this.options.HTMLAttributes.colorLink,
          autoBoldLinks: this.options.HTMLAttributes.autoBoldLinks,
          tooltipId: nanoid(),
        }),
        this.editor.schema
      ),
      nodePasteRuleHardBreak(
        /\u2028/g,
        "hardBreak",
        undefined,
        this.editor.schema
      ),
      nodePasteRuleParagraph(undefined, this.editor.schema),
      nodePasteRuleTrim(this.editor),
    ];
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true;

              // On deleting, insert no text
              tr.insertText("", pos, pos + node.nodeSize);
              return false;
            }
          });

          return isMention;
        }),
      Enter: () =>
        this.editor.commands.command(({ tr, state }) => {
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              const hasUnderline = node.marks.find(
                (m) => m.type === state.schema.marks["underline"]
              );

              if (hasUnderline) {
                tr.removeStoredMark(state.schema.marks["underline"]);
              }

              const hasBold = node.marks.find(
                (m) => m.type === state.schema.marks["bold"]
              );

              if (hasBold) {
                tr.removeStoredMark(state.schema.marks["bold"]);
              }

              return false;
            }
          });

          return false;
        }),
    };
  },

  addCommands() {
    return {
      addURLInRange:
        ({ from, to, marks, ...attributes }) =>
        ({ commands, state }) => {
          return commands.command(({ tr }) => {
            const { id, label, colorLink, tooltipId, autoBoldLinks } =
              attributes;
            let node = ProseMirrorNode.fromJSON(state.doc.type.schema, {
              type: "mention",
              attrs: { id, label, colorLink, tooltipId },
              marks: [
                ...marks.map((mark) => ({
                  type: mark,
                })),
                { type: "underline", attrs: { color: colorLink } },
                ...(autoBoldLinks ? [{ type: "bold" }] : []),
              ],
            });

            const fragment = Fragment.fromArray([node]);
            const slice = Slice.maxOpen(fragment);

            tr.replaceRange(from, to, slice);

            return true;
          });
        },
      addURL:
        ({ marks, ...attributes }) =>
        ({ commands, state }) => {
          return commands.command(({ tr }) => {
            // Due to the limitations "replaceSelectionWith" (I don't know if there's a bug on it)
            // I'm not passing the marks here.
            let node = ProseMirrorNode.fromJSON(state.doc.type.schema, {
              type: "mention",
              attrs: { ...attributes },
            });

            tr.replaceSelectionWith(node);

            return true;
          });
        },
      removeURL:
        () =>
        ({ commands }) => {
          return commands.command(({ tr }) => {
            tr.deleteSelection();

            return true;
          });
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
